import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { toast } from 'react-toastify';
import type { WorkbookInstance } from '@fortune-sheet/react';
import { locale } from '@fortune-sheet/core';
import {
    getInternals,
    genarate,
    updateCell,
    expandRowsAndColumns,
    execFunctionGroup,
    isEditingInContext,
    clearFormulaAt,
    isPartiallyMerged,
    type WorkbookInternals,
} from './workbookInternals';
import { buildPastePlan, mergePasteCell, type PasteMode } from './pasteSpecial';
import { isGridKeyTarget } from './shortcutKeymap';
import { FortuneCell } from '../types';

const ARM_WINDOW_MS = 1000;
const COPY_MARKER = 'fortune-copy-action-table';
const CLIPBOARD_DENIED_MESSAGE = 'Allow clipboard access, or press Ctrl+Shift+V to paste values.';

export interface CapturedPaste {
    isInternal: boolean;
    sourceGrid: (FortuneCell | null)[][];
    sourceOrigin?: { r: number; c: number };
}

/** Types one clipboard token exactly like a native paste would (core's own
 *  `genarate`) — so an external numeric token lands as a number, not
 *  the plain string a native paste would never produce. */
const cellFromToken = (token: string): FortuneCell | null => {
    if (token === '') return null;
    const generated = genarate?.(token);
    if (!generated) return null;
    const [m, ct, v] = generated;
    return { m, ct, v };
};

/** Parses QUOTED TSV — the shape Excel/Google Sheets actually put on the
 *  clipboard, not plain tab/newline-separated text: a field containing a
 *  tab, newline, or double-quote is wrapped in "..." with any literal "
 *  doubled (""). Character-by-character (not split('\t')/split('\n')) so an
 *  embedded \t or \n inside a quoted field is never mistaken for a real
 *  field/row boundary. */
const parseQuotedTsv = (text: string): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;
    let i = 0;
    while (i < text.length) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"' && text[i + 1] === '"') {
                field += '"';
                i += 2;
            } else if (ch === '"') {
                inQuotes = false;
                i += 1;
            } else {
                field += ch;
                i += 1;
            }
            continue;
        }
        if (ch === '"' && field === '') {
            inQuotes = true;
            i += 1;
        } else if (ch === '\t') {
            row.push(field);
            field = '';
            i += 1;
        } else if (ch === '\n' || (ch === '\r' && text[i + 1] === '\n')) {
            row.push(field);
            rows.push(row);
            row = [];
            field = '';
            i += ch === '\r' ? 2 : 1;
        } else {
            field += ch;
            i += 1;
        }
    }
    row.push(field);
    rows.push(row);
    // No trailing blank row from a final newline (matches a plain split).
    const last = rows[rows.length - 1];
    if (last && last.length === 1 && last[0] === '') rows.pop();
    return rows;
};

/** Splits plain-clipboard text into a grid of value-only cells. */
const gridFromPlainText = (text: string): (FortuneCell | null)[][] => parseQuotedTsv(text).map((row) => row.map(cellFromToken));

/** Fortune resets `luckysheet_copy_save` to this SENTINEL — not null/undefined
 *  — when a copy/paint-mode selection is cancelled (core's cancelPaintModel,
 *  e.g. pressing Escape after Ctrl+C, or starting an edit elsewhere):
 *  `{dataSheetId: '', copyRange: [{row: [0], column: [0]}], ...}`. A real
 *  copy's `dataSheetId` is never empty — the one field the sentinel can't
 *  fake. */
const isCopySaveSentinel = (copySave: { dataSheetId: string } | undefined): boolean => !copySave || copySave.dataSheetId === '';

/** Reads the grid's OWN internal-copy state directly — no clipboard access,
 *  no native paste event needed. `null` when the last in-app copy is gone
 *  (cancelled, a fresh external copy, or none at all). Reads from the copy's
 *  OWN source sheet (`copySave.dataSheetId`), not whichever sheet is
 *  currently active — a copy made on Sheet1 then viewed from Sheet2 must
 *  still paste Sheet1's cells. */
const internalSourceFromGrid = (wb: WorkbookInstance | null, internals: WorkbookInternals | null): CapturedPaste | null => {
    const copySave = internals?.getContext().luckysheet_copy_save;
    if (isCopySaveSentinel(copySave)) return null;
    const range = copySave!.copyRange[0];
    const sourceGrid = wb
        ? (wb.getCellsByRange({ row: range.row, column: range.column }, { id: copySave!.dataSheetId }) as (FortuneCell | null)[][])
        : [];
    return { isInternal: true, sourceGrid, sourceOrigin: { r: range.row[0], c: range.column[0] } };
};

/** The REAL current clipboard's html/text — the async equivalent of the
 *  native-paste handler's synchronous `e.clipboardData.getData(...)` reads,
 *  for `openFromKeyboard`'s freshness check: a live-looking
 *  `luckysheet_copy_save` doesn't prove the OS clipboard
 *  still holds it — the user may have copied something else externally
 *  since. `null` means the read itself was denied/unsupported (no way to
 *  verify against the real clipboard at all); `read()` for text/html is
 *  used only where available (older browsers only expose `readText`) and
 *  its own failure just leaves `html` empty, not the whole call. */
const readClipboardFreshness = async (): Promise<{ html: string; text: string } | null> => {
    if (!navigator.clipboard?.readText) return null;
    let text: string;
    try {
        text = await navigator.clipboard.readText();
    } catch {
        return null;
    }
    let html = '';
    if (navigator.clipboard.read) {
        try {
            const items = await navigator.clipboard.read();
            const htmlItem = items.find((item) => item.types.includes('text/html'));
            if (htmlItem) html = await (await htmlItem.getType('text/html')).text();
        } catch {
            // text/html unavailable (older browser, or just this one MIME
            // type not granted) — the genuine-copy marker check below still
            // applies against `text`.
        }
    }
    return { html, text };
};

/** True when `clip` (the REAL current clipboard) matches what Fortune's own
 *  copy action wrote — the same OR the native-paste handler's `isInternal`
 *  check uses. `genuineMarker` is the plain text a CONFIRMED genuine grid
 *  copy wrote (see onCopyOrCut) — never a live re-read of shared state,
 *  since core's `clipboard.writeHtml` (`#fortune-copy-content`'s own
 *  textContent, and sessionStorage's 'localClipboard') is rewritten on
 *  EVERY call, including right-click "Paste"'s (core:61477) — a live read
 *  would wrongly "match" a stale grid copy against a paste's own echo of
 *  the external clipboard back onto itself. */
const clipboardMatchesInternalCopy = (clip: { html: string; text: string }, genuineMarker: string | null): boolean =>
    clip.html.includes(COPY_MARKER) || (genuineMarker != null && genuineMarker === clip.text);

/** The core write: builds the paste plan for `captured` against the current
 *  selection and applies it in one `setContextWithProduce` — shared by the
 *  dialog's own Apply (`apply`) and Ctrl/Cmd+Shift+V's direct values-paste
 *  (no dialog, no React state involved). */
const applyPastePlan = (
    wb: WorkbookInstance,
    internals: WorkbookInternals,
    captured: CapturedPaste,
    mode: PasteMode,
    transpose: boolean,
): void => {
    const selection = wb.getSelection?.();
    const dest = selection?.[0] ? { r: selection[0].row[0], c: selection[0].column[0] } : { r: 0, c: 0 };
    const plan = buildPastePlan(captured.sourceGrid, dest, { mode, transpose, sourceOrigin: captured.sourceOrigin });
    if (!plan.length) return;

    // Matches native paste (core:79218-79222/79413-79418): refuse outright
    // when the pasted rectangle partially overlaps an existing merge, rather
    // than silently unmerging it (the grid renders a merge off the cell's own
    // `mc`, export writes it from config.merge — divergence otherwise).
    const ctx = internals.getContext();
    const rows = plan.map((p) => p.r);
    const cols = plan.map((p) => p.c);
    if (isPartiallyMerged(ctx, Math.min(...rows), Math.max(...rows), Math.min(...cols), Math.max(...cols))) {
        toast.error(locale(ctx).generalDialog.partiallyError);
        return;
    }

    internals.setContextWithProduce((d) => {
        const sheet = d.luckysheetfile.find((s) => s.id === d.currentSheetId);
        if (!sheet) return;
        if (!sheet.data) sheet.data = [];
        if (!sheet.data.length) sheet.data.push([]); // expandRowsAndColumns needs data[0] to exist
        // A block pasted past the current extent must grow the grid
        // FIRST (mirrors Fortune's own paste) — writing straight past it
        // leaves holey rows (undefined gaps) and a stale sheet.row/column.
        let maxR = 0;
        let maxC = 0;
        plan.forEach((p) => {
            if (p.r > maxR) maxR = p.r;
            if (p.c > maxC) maxC = p.c;
        });
        const addRows = maxR + 1 - sheet.data.length;
        const addCols = maxC + 1 - (sheet.data[0]?.length ?? 0);
        if (addRows > 0 || addCols > 0) {
            expandRowsAndColumns?.(sheet.data, Math.max(addRows, 0), Math.max(addCols, 0));
            sheet.row = Math.max(sheet.row ?? 0, sheet.data.length);
            sheet.column = Math.max(sheet.column ?? 0, sheet.data[0]?.length ?? 0);
        }
        plan.forEach(({ r, c, cell }) => {
            if (!sheet.data![r]) sheet.data![r] = [];
            const oldDest = sheet.data![r][c];
            // Values always drops a formula destination's `f` (mergePasteCell
            // never carries it through); All does too when the source itself
            // is a literal (`{...projected}` has no `f` to keep it alive).
            // clearFormulaAt mirrors updateCell's own full cleanup for that
            // same transition — both the calcChain entry AND the
            // formulaCache.formulaCellInfoMap entry, or a later edit to the
            // formula's OLD dependency can resurrect it via that separate,
            // rarely-invalidated cache.
            const destFormulaDropped = oldDest?.f && (mode === 'values' || (mode === 'all' && !cell?.f));
            if (destFormulaDropped) clearFormulaAt(d, r, c, d.currentSheetId);
            sheet.data![r][c] = mergePasteCell(oldDest, cell, mode);
            // 'all' carries the source's stale cached v/m for a formula
            // cell (mergePasteCell doesn't recompute) — route it through
            // the library's own commit so it's evaluated and registered
            // into calcChain, exactly like fillSelection's fix. `f` is
            // cleared first: updateCell no-ops when the incoming formula
            // string already equals the cell's current one, which the
            // mergePasteCell write just above made true.
            if (mode === 'all' && cell?.f) {
                delete sheet.data![r][c]!.f;
                updateCell?.(d, r, c, undefined, cell.f);
            } else if (mode !== 'formats') {
                // A direct sheet.data write (Values mode, or All mode's own
                // non-formula cells)
                // never recalculates a FORMULA cell that depends on (r, c) —
                // execFunctionGroup is the same dependent-recalc trigger
                // updateCell's own plain-value commit path calls internally
                // (core:60973-60981), reachable standalone so mergePasteCell's
                // own ct/styling-preservation stays untouched (routing the
                // write itself through updateCell would lose that). Formats
                // mode never changes v, so nothing depends on it to recalc.
                execFunctionGroup?.(d, r, c, sheet.data![r][c]?.v);
            }
        });
    });
};

export interface UsePasteSpecialArgs {
    /** Registers the capture listener while true (mirrors Lane B). */
    open: boolean;
    workbookRef: RefObject<WorkbookInstance>;
    historyOpen: boolean;
}

export interface UsePasteSpecialReturn {
    /** Ctrl/Cmd+Shift+V — arms the one-shot flag WITHOUT `preventDefault` (so
     *  the browser still delivers the following native paste event); that
     *  paste applies VALUES directly, with no dialog. */
    armValuesPaste: () => void;
    /** Ctrl+Alt+V / Cmd+Ctrl+V / Ctrl+Option+V / Cmd+Option+V — opens the
     *  dialog immediately: a KNOWN-fresh internal copy resolves straight
     *  from the grid, no clipboard access, no prompt (a grid copy goes stale
     *  on window blur/tab-hide/a non-grid copy since). Otherwise
     *  falls back to `navigator.clipboard.readText()` (the one-time
     *  permission prompt), showing a toast on denial. */
    openFromKeyboard: () => void;
    dialogOpen: boolean;
    isInternalSource: boolean;
    closeDialog: () => void;
    apply: (mode: PasteMode, transpose: boolean) => void;
}

/**
 * Ctrl/Cmd+Shift+V arms a one-shot flag for the VALUES paste — the very next
 * `paste` event within 1000 ms is captured (`stopImmediatePropagation` — the
 * library's own bubble-phase paste handler never runs — plus `preventDefault`,
 * since stopping the library's handler also stops the `preventDefault` IT
 * would normally have done) and applies values directly, no dialog. The
 * dialog itself now opens from a SEPARATE key (`openFromKeyboard`) that needs
 * no native paste event at all.
 */
export const usePasteSpecial = ({ open, workbookRef, historyOpen }: UsePasteSpecialArgs): UsePasteSpecialReturn => {
    const armedUntilRef = useRef<number | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pasteData, setPasteData] = useState<CapturedPaste | null>(null);
    const historyOpenRef = useRef(historyOpen);
    historyOpenRef.current = historyOpen;

    const armValuesPaste = useCallback(() => {
        armedUntilRef.current = Date.now() + ARM_WINDOW_MS;
    }, []);

    // openFromKeyboard reading the real clipboard on EVERY invocation prompts
    // even for an internal copy, which should never prompt. Tracks
    // freshness WITHOUT reading the clipboard: a
    // grid-originated copy records "fresh as of now"; a window blur, the tab
    // going hidden, or any copy/cut NOT originating from the grid (a real
    // signal the OS clipboard may have changed since) marks it stale again.
    // openFromKeyboard trusts the internal copy with no clipboard
    // read/prompt only while this is still fresh — otherwise it falls back
    // to the existing clipboard-read freshness check below, exactly as
    // before.
    const isGridCopyFreshRef = useRef(false);
    // Set by onKeyDown below when the MOST RECENT keydown was a genuine grid
    // copy (Ctrl/Cmd+C, grid focus, not editing a cell). Cleared by a
    // setTimeout(0) scheduled from the SAME keydown, not by the first copy
    // event it sees — Fortune's own handleCopy never calls preventDefault,
    // so a real Cmd/Ctrl+C fires its own synchronous copy (execCommand)
    // AND the browser's native one on the same still-focused element, ~4ms
    // apart. Consuming the flag on the first would mark the second stale,
    // forcing a clipboard-read prompt on every single Cmd+C.
    // A right-click paste's own synthetic copy is a LATER, separate click —
    // by then this has already cleared. See onCopyOrCut's own comment for
    // why this replaced a luckysheet_copy_save/sessionStorage signature
    // check.
    const pendingGridCopyKeydownRef = useRef(false);
    const pendingGridCopyClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The plain text a CONFIRMED genuine grid copy wrote, captured once per
    // genuine copy (see onCopyOrCut) — the freshness fallback's only
    // trustworthy "what did OUR copy contain" snapshot. NOT cleared by
    // blur/tab-hide/a foreign copy — the clipboard-MATCH fallback
    // (clipboardMatchesInternalCopy) always re-verifies it against a live
    // clipboard read, so a stale-but-still-accurate marker is harmless there.
    const genuineCopyMarkerRef = useRef<string | null>(null);
    // True once a REAL copy/cut that did NOT originate from the grid has
    // happened since the marker above was captured — the strongest possible
    // proof the OS clipboard has genuinely changed. Reset whenever a fresh
    // genuine grid copy is captured. Gates ONLY the clipboard-DENIED fallback
    // in openFromKeyboard, which has no live clipboard read to verify
    // against and would otherwise keep trusting an old marker forever.
    const foreignCopySinceMarkerRef = useRef(false);

    useEffect(() => {
        if (!open) return undefined;
        const markStale = (): void => {
            isGridCopyFreshRef.current = false;
        };
        // Only signal a paste action can't fake: right-click "Paste" (core's
        // handlePasteByClick) fires from a menu click, never a keydown.
        // `e.code` (the physical key position) rather than `e.key` (the
        // character produced) — a non-Latin/Dvorak layout's Ctrl+C never
        // produces the character "c".
        const onKeyDown = (e: KeyboardEvent): void => {
            if (pendingGridCopyClearTimeoutRef.current != null) clearTimeout(pendingGridCopyClearTimeoutRef.current);
            const isCopyChord = (e.ctrlKey || e.metaKey) && e.code === 'KeyC';
            const notEditing = !isEditingInContext(getInternals(workbookRef.current)?.getContext());
            const isGenuineCopyKeydown = isCopyChord && isGridKeyTarget(e.target) && notEditing;
            pendingGridCopyKeydownRef.current = isGenuineCopyKeydown;
            if (isGenuineCopyKeydown) {
                pendingGridCopyClearTimeoutRef.current = setTimeout(() => {
                    pendingGridCopyKeydownRef.current = false;
                }, 0);
            }
        };
        // Fortune's OWN grid copy always fires a native `copy` event on its
        // hidden `#fortune-copy-content` element (core's `clipboard.writeHtml`
        // — a focus + execCommand('copy') on that element specifically); it
        // has no native `cut` action at all. So a real `cut` event, or a
        // `copy` from anywhere else (the in-cell editor, the formula bar,
        // any other input), is never the grid's own copy and must go stale.
        //
        // Right-click "Paste" (core's handlePasteByClick) ALSO writes to
        // #fortune-copy-content via the SAME clipboard.writeHtml, firing this
        // SAME synthetic copy event on a PASTE action — target alone can't
        // tell the two apart. Nor can luckysheet_copy_save (a paste never
        // touches it) or a sessionStorage marker match (writeHtml rewrites
        // both #fortune-copy-content's own textContent AND sessionStorage on
        // EVERY call, including a paste's — core:61477). Only a copy event
        // while a genuine grid copy keydown is still pending (onKeyDown
        // above) is trusted — read, never consumed here, so the SAME
        // keypress's second (native) copy event still counts. That event's
        // own innerText (core's own preference over textContent, core:61476
        // — multi-cell copies can differ), trimmed to match how
        // execCommand('copy') serializes it onto the OS clipboard, set
        // BEFORE execCommand('copy') fires it, is snapshotted as the genuine
        // marker — never a later live read, which a subsequent paste's own
        // writeHtml call could have overwritten.
        const onCopyOrCut = (e: Event): void => {
            const copyContent = document.getElementById('fortune-copy-content');
            const isGridCopyTarget = e.type === 'copy' && copyContent != null && e.target instanceof Node && copyContent.contains(e.target);
            if (isGridCopyTarget && pendingGridCopyKeydownRef.current) {
                isGridCopyFreshRef.current = true;
                genuineCopyMarkerRef.current = (copyContent.innerText || copyContent.textContent || '').trim();
                foreignCopySinceMarkerRef.current = false;
            } else {
                markStale();
                foreignCopySinceMarkerRef.current = true;
            }
        };
        const onVisibilityChange = (): void => {
            if (document.visibilityState === 'hidden') markStale();
        };
        window.addEventListener('blur', markStale);
        document.addEventListener('visibilitychange', onVisibilityChange);
        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('copy', onCopyOrCut, true);
        document.addEventListener('cut', onCopyOrCut, true);
        return () => {
            if (pendingGridCopyClearTimeoutRef.current != null) clearTimeout(pendingGridCopyClearTimeoutRef.current);
            window.removeEventListener('blur', markStale);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('copy', onCopyOrCut, true);
            document.removeEventListener('cut', onCopyOrCut, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;

        const handler = (e: ClipboardEvent): void => {
            const until = armedUntilRef.current;
            if (until == null || Date.now() > until) return;
            armedUntilRef.current = null;
            e.stopImmediatePropagation();
            e.preventDefault();

            if (historyOpenRef.current) return; // history preview — no-op like every other mutating action

            const wb = workbookRef.current;
            const internals = getInternals(wb);
            if (!wb || !internals) return;

            const html = e.clipboardData?.getData('text/html') ?? '';
            const text = e.clipboardData?.getData('text/plain') ?? '';
            const copySave = internals.getContext().luckysheet_copy_save;
            const isInternal = Boolean(
                copySave && !isCopySaveSentinel(copySave) && clipboardMatchesInternalCopy({ html, text }, genuineCopyMarkerRef.current),
            );

            // `isInternal` is only true when copySave is present and not the
            // cancelled-copy sentinel, so this never falls through to null.
            const captured: CapturedPaste = isInternal
                ? (internalSourceFromGrid(wb, internals) as CapturedPaste)
                : { isInternal: false, sourceGrid: gridFromPlainText(text) };
            applyPastePlan(wb, internals, captured, 'values', false);
        };

        document.addEventListener('paste', handler, true);
        return () => document.removeEventListener('paste', handler, true);
    }, [open, workbookRef]);

    const closeDialog = useCallback(() => {
        setDialogOpen(false);
        setPasteData(null);
    }, []);

    const openFromKeyboard = useCallback(() => {
        if (historyOpenRef.current) return; // history preview — no-op like every other mutating action

        const wb = workbookRef.current;
        const internals = getInternals(wb);
        const openInternal = (): boolean => {
            const internalSource = internalSourceFromGrid(wb, internals);
            if (!internalSource) return false;
            setPasteData(internalSource);
            setDialogOpen(true);
            return true;
        };
        const copySave = internals?.getContext().luckysheet_copy_save;
        const looksInternal = Boolean(copySave && !isCopySaveSentinel(copySave));

        // A grid copy KNOWN fresh (no blur/tab-hide/non-grid copy since) is
        // trusted directly — no clipboard read, no prompt. Only a
        // stale or absent freshness signal falls through to the
        // clipboard-read check below.
        if (looksInternal && isGridCopyFreshRef.current && openInternal()) return;

        readClipboardFreshness().then((clip) => {
            if (clip) {
                // A live-looking internal copy doesn't prove the OS
                // clipboard still holds it — this is the SAME freshness rule
                // the native-paste handler uses (COPY_MARKER / genuine-copy
                // marker), so a copy since cleared (Esc) or superseded by an
                // external copy never pastes stale grid content.
                if (looksInternal && clipboardMatchesInternalCopy(clip, genuineCopyMarkerRef.current) && openInternal()) return;
                setPasteData({ isInternal: false, sourceGrid: gridFromPlainText(clip.text) });
                setDialogOpen(true);
                return;
            }
            // Unsupported (older browser/insecure context) or the read
            // itself was denied. No way to verify against the real
            // clipboard — fall back to the internal copy only when a
            // CONFIRMED genuine grid copy (onCopyOrCut) is the most recent
            // thing we know about AND no real foreign copy/cut has happened
            // since (a definite sign the OS clipboard moved on — e.g. a copy
            // from the formula bar or cell editor); otherwise there's no
            // basis to trust it either.
            if (looksInternal && genuineCopyMarkerRef.current != null && !foreignCopySinceMarkerRef.current && openInternal()) return;
            toast.info(CLIPBOARD_DENIED_MESSAGE);
        });
    }, [workbookRef]);

    const apply = useCallback(
        (mode: PasteMode, transpose: boolean) => {
            const wb = workbookRef.current;
            const internals = getInternals(wb);
            if (wb && internals && pasteData) applyPastePlan(wb, internals, pasteData, mode, transpose);
            closeDialog();
        },
        [workbookRef, pasteData, closeDialog],
    );

    return { armValuesPaste, openFromKeyboard, dialogOpen, isInternalSource: pasteData?.isInternal ?? false, closeDialog, apply };
};
